import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useUser } from "@clerk/react";
import { GenerateForm } from "@/components/generate/generate-form";
import { Zap, Clock, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { trackViewContent } from "@/services/analytics";

interface PingResponse {
  success: boolean;
  asset_url?: string;
  input_text?: string;
}

// Dedupe the partner-session claim across React StrictMode double-mounts and
// component remounts: one in-flight /ping per cid for the life of the page.
// The backend /ping is idempotent, so a real page refresh (fresh module state)
// simply re-claims the same session and re-issues the cookie.
const pingCache = new Map<string, Promise<PingResponse>>();
function claimPartnerSession(cid: string, fbclid: string | null): Promise<PingResponse> {
  let pending = pingCache.get(cid);
  if (!pending) {
    const params = new URLSearchParams({ cid });
    if (fbclid) params.set("fbclid", fbclid);
    pending = api
      .get<PingResponse>(`/ping?${params.toString()}`)
      .catch(() => ({ success: false }) as PingResponse);
    pingCache.set(cid, pending);
  }
  return pending;
}

export function ImageToVideoPage() {
  const { user } = useUser();
  const [searchParams] = useSearchParams();
  const cid = searchParams.get("affiliate_click_id");
  const fbclid = searchParams.get("fbclid");

  useEffect(() => {
    trackViewContent("Image to Video", user?.id, { funnel_stage: "create" });
  }, []);

  const [prefill, setPrefill] = useState<{ imageUrl: string; prompt: string } | null>(null);
  const [loading, setLoading] = useState(!!cid);

  useEffect(() => {
    if (!cid) return;
    let cancelled = false;

    claimPartnerSession(cid, fbclid)
      .then((res) => {
        if (cancelled) return;
        if (res.success && res.asset_url) {
          setPrefill({ imageUrl: res.asset_url, prompt: res.input_text ?? "" });
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [cid]);

  return (
    <div className="p-5 md:p-8 max-w-4xl mx-auto">
      <div className="mb-6 animate-fade-in-up">
        <h1 className="text-2xl md:text-3xl font-extrabold text-[#2d3127] tracking-tight mb-2">
          Image to Video
        </h1>
        <p className="text-sm text-[#8a8570]">
          Upload an image and bring it to life with AI Video
        </p>
        <div className="flex flex-wrap items-center gap-5 mt-3 text-xs text-[#8a8570]">
          <span className="flex items-center gap-1.5">
            <Zap className="size-3 text-amber-600" />
            Standard & Premium
          </span>
          <span className="flex items-center gap-1.5">
            <Clock className="size-3 text-emerald-500" />
            ~60s creation time
          </span>
        </div>
      </div>

      <div className="mb-12 animate-fade-in-up delay-100">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="size-6 animate-spin text-[#b5b0a0]" />
          </div>
        ) : (
          <GenerateForm
            initialImageUrl={prefill?.imageUrl}
            initialPrompt={prefill?.prompt}
            forceAuth={!!prefill}
            affiliateFunnel={!!cid}
          />
        )}
      </div>
    </div>
  );
}
